import { InputNumber, Select, Table, Tag } from "antd";
import { get, isNil } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { CURRENCY_SYMBOL, PRICING_UNITS, TAG_COLORS } from "utils/constants";
import {
  PARTNER_TARIFF_KEY,
  domainOfFactor,
  hasPartnerTariff,
} from "./pricingInputDomain";

const PRICING_UNIT_OPTIONS = [
  {
    value: PRICING_UNITS.EUR,
    labelKey: "CONFIGURE.PRICING_INPUT.UNIT_EUR_SUFFIX",
  },
  { value: PRICING_UNITS.PMSS, label: CURRENCY_SYMBOL.PMSS },
];

const PricingInput = ({
  value,
  onChange,
  label,
  ageBrackets,
  regimes,
  zones,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const factors = get(value, "ratingFactors") || [];
  const currentUnit = get(value, "unit", PRICING_UNITS.EUR);
  const unitSuffix =
    currentUnit === PRICING_UNITS.PMSS
      ? CURRENCY_SYMBOL.PMSS
      : factors.includes("duration")
        ? "€"
        : t("CONFIGURE.PRICING_INPUT.UNIT_EUR_SUFFIX");

  const factorLabel = (key) => {
    const labelKey = {
      age: "CONFIGURE.PRICING_INPUT.FACTOR_AGE",
      regime: "CONFIGURE.PRICING_INPUT.FACTOR_REGIME",
      zone: "CONFIGURE.PRICING_INPUT.FACTOR_ZONE",
      department: "CONFIGURE.PRICING_INPUT.FACTOR_DEPARTMENT",
      duration: "CONFIGURE.PRICING_INPUT.FACTOR_DURATION",
    }[key];
    if (labelKey) {
      return t(labelKey);
    }
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  const durationLabel = (key) => {
    const labels = t("CONFIGURE.PRICING_INPUT.DURATIONS", {
      returnObjects: true,
    });
    return get(labels, key, key);
  };

  const emit = (next) => onChange(next);
  const handleUnitChange = (unit) => emit({ ...value, unit });
  const setRate = (key, price) =>
    emit({ ...value, rates: { ...get(value, "rates", {}), [key]: price } });
  const rateValue = (key) => {
    const rate = get(value, ["rates", key]);
    return isNil(rate) ? "" : rate;
  };

  const domainOf = (factorKey) =>
    domainOfFactor(value, factorKey, { ageBrackets, regimes, zones });

  const priceCell = (key) => (
    <InputNumber
      size="small"
      min={0}
      step={0.01}
      value={rateValue(key)}
      onChange={(price) => setRate(key, price)}
      className="w-100"
      placeholder="0.00"
    />
  );

  const renderFlat = () => (
    <InputNumber
      min={0}
      step={0.01}
      value={rateValue("")}
      onChange={(price) => setRate("", price)}
      className="pricing-input__flat-input"
      placeholder="0.00"
      addonAfter={unitSuffix}
    />
  );

  const renderSingle = () => {
    const rows = domainOf(factors[0]);
    const showPartner = hasPartnerTariff(get(value, "rates"));
    const labelColumn = {
      title: factorLabel(factors[0]),
      dataIndex: "label",
      width: 160,
      render: (text, row) => (
        <strong>
          {factors[0] === "duration" ? durationLabel(row.key) : text}
        </strong>
      ),
    };
    const columns = showPartner
      ? [
          labelColumn,
          {
            title: t("CONFIGURE.PRICING_INPUT.RATE_PUBLIC", {
              suffix: unitSuffix,
            }),
            render: (_, row) => priceCell(row.key),
          },
          {
            title: t("CONFIGURE.PRICING_INPUT.RATE_PARTNER", {
              suffix: unitSuffix,
            }),
            render: (_, row) => priceCell(`${row.key}|${PARTNER_TARIFF_KEY}`),
          },
        ]
      : [
          labelColumn,
          {
            title: t("CONFIGURE.PRICING_INPUT.RATE", { suffix: unitSuffix }),
            render: (_, row) => priceCell(row.key),
          },
        ];
    return (
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={rows}
        columns={columns}
      />
    );
  };

  const renderMatrix = () => {
    const cols = domainOf(factors[0]);
    const rows = domainOf(factors[1]);
    return (
      <Table
        size="small"
        pagination={false}
        rowKey="key"
        dataSource={rows}
        scroll={{ x: true }}
        columns={[
          {
            title: factorLabel(factors[1]),
            dataIndex: "label",
            width: 140,
            fixed: "left",
            render: (text) => <strong>{text}</strong>,
          },
          ...cols.map((col) => ({
            title: `${col.label} (${unitSuffix})`,
            render: (_, row) => priceCell(`${col.key}|${row.key}`),
          })),
        ]}
      />
    );
  };

  const renderCombinations = () => {
    const domains = factors.map((f) => domainOf(f));
    const rows = domains.reduce(
      (acc, domain) => acc.flatMap((combo) => domain.map((d) => [...combo, d])),
      [[]],
    );
    return (
      <Table
        size="small"
        pagination={false}
        rowKey={(combo) => combo.map((d) => d.key).join("|")}
        dataSource={rows}
        scroll={{ x: true }}
        columns={[
          ...factors.map((f, i) => ({
            title: factorLabel(f),
            render: (_, combo) => <strong>{combo[i].label}</strong>,
          })),
          {
            title: t("CONFIGURE.PRICING_INPUT.RATE", { suffix: unitSuffix }),
            render: (_, combo) => priceCell(combo.map((d) => d.key).join("|")),
          },
        ]}
      />
    );
  };

  const renderGrid = () => {
    if (factors.length === 0) return renderFlat();
    if (factors.length === 1) return renderSingle();
    if (factors.length === 2) return renderMatrix();
    return renderCombinations();
  };

  return (
    <div>
      {label && <div className="pricing-input__label">{label}</div>}

      <div className="pricing-input__section">
        <span className="pricing-input__field-label">
          {t("CONFIGURE.PRICING_INPUT.PRICING_INFO")}
        </span>
        {factors.length === 0 ? (
          <Tag>{t("CONFIGURE.TAGS.FIXED_PRICE")}</Tag>
        ) : (
          factors.map((f) => (
            <Tag key={f} color={TAG_COLORS.BLUE}>
              {factorLabel(f)}
            </Tag>
          ))
        )}
      </div>

      <div className="pricing-input__section">
        <span className="pricing-input__field-label">
          {t("CONFIGURE.PRICING_INPUT.UNIT")}
        </span>
        <Select
          size="small"
          value={currentUnit}
          onChange={handleUnitChange}
          className="pricing-input__unit-select"
        >
          {PRICING_UNIT_OPTIONS.map((u) => (
            <Select.Option key={u.value} value={u.value}>
              {u.labelKey ? t(u.labelKey) : u.label}
            </Select.Option>
          ))}
        </Select>
      </div>

      <div className="pricing-grid">{renderGrid()}</div>
    </div>
  );
};

export default PricingInput;
